import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import SplitText from 'gsap/SplitText';
import { onWidthResize, offWidthResize } from '../utils/resize.js';
gsap.registerPlugin(ScrollTrigger, SplitText);

export default class ArticleHero {
  constructor(container) {
    this.container = container;
    this.shareButton = container.querySelector('[data-share-url]');
    this.category = container.querySelector('.article-hero__category');
    this.title = container.querySelector('.article-hero__title');
    this.share = container.querySelector('.article-hero__share');
    this.tagItems = container.querySelectorAll('.article-hero__tags li');
    this.media = container.querySelector('.article-hero__media');
    this.mediaImg = this.media?.querySelector('img');

    this.init();

    if (this.shareButton) {
      this.shareButton.addEventListener('click', this.onShareClick);
    }
  }

  init = () => {
    if (this.media) {
      gsap.fromTo(
        this.media,
        { clipPath: 'inset(0% 0% 100% 0%)' },
        {
          clipPath: 'inset(0% 0% 0% 0%)',
          duration: 1.2,
          delay: 0.3,
          ease: 'power3.inOut',
          scrollTrigger: { trigger: this.container },
        }
      );

      if (this.mediaImg) {
        gsap.fromTo(
          this.mediaImg,
          { scale: 1.15 },
          {
            scale: 1,
            duration: 1.2,
            delay: 0.3,
            ease: 'power3.inOut',
            scrollTrigger: { trigger: this.container },
          }
        );
      }
    }

    if (this.category) {
      gsap.fromTo(
        this.category,
        { opacity: 0 },
        {
          opacity: 1,
          duration: 0.6,
          delay: 0.7,
          ease: 'power2.out',
          scrollTrigger: { trigger: this.container },
        }
      );
    }

    if (this.title) {
      this.titleSplit_lines = new SplitText(this.title, { type: 'lines,words', linesClass: 'lineParent' });
      this.titleSplit_words = new SplitText(this.title.querySelectorAll('.lineParent'), { type: 'words', });

      gsap.fromTo(
        this.titleSplit_words.words,
        { yPercent: 100, },
        {
          yPercent: 0,
          duration: 0.6,
          delay: 0.9,
          stagger: { each: 0.07, ease: 'power.out', from: 'start' },
          ease: 'power3.out',
          scrollTrigger: { trigger: this.container },
          onComplete: () => {
            onWidthResize(this.onTitleResize);
          },
        }
      );
    }

    if (this.share) {
      gsap.fromTo(
        this.share,
        { opacity: 0 },
        {
          opacity: 1,
          duration: 0.6,
          delay: 0.6,
          ease: 'power2.out',
          scrollTrigger: { trigger: this.container },
        }
      );
    }

    if (this.tagItems.length) {
      gsap.fromTo(
        this.tagItems,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          delay: 1.7,
          stagger: { each: 0.08, ease: 'power2.out', from: 'start' },
          ease: 'power2.out',
          scrollTrigger: { trigger: this.container },
        }
      );
    }
  }

  onTitleResize = () => {
    this.titleSplit_lines?.revert();
    this.titleSplit_words?.revert();
    offWidthResize(this.onTitleResize);
  }

  onShareClick = async () => {
    const url = this.shareButton.dataset.shareUrl;
    const title = this.shareButton.dataset.shareTitle;
    if (!url) return;

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }

    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(url);
        this.flashCopied();
        return;
      } catch (err) {
        // fall through
      }
    }

    window.prompt('Copy link:', url);
  };

  flashCopied = () => {
    const original = this.shareButton.getAttribute('aria-label');
    this.shareButton.setAttribute('aria-label', 'Link copied');
    this.shareButton.classList.add('is-copied');
    setTimeout(() => {
      this.shareButton.setAttribute('aria-label', original);
      this.shareButton.classList.remove('is-copied');
    }, 1800);
  };
}
